import React, { useState } from "react";
import { Layout, message } from "antd";
import { Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { subscribeMarketingEmail, updateEmailPreference } from "../../services";
import './footer.scss';

const { Footer: AntdFooter } = Layout;

const footerColumns = [
  {
    title: 'Shop',
    links: [
      { label: 'All Jewelry', to: '/products' },
      { label: 'Rings', to: '/products/types/Rings' },
      { label: 'Bracelets', to: '/products/types/Bracelets' },
      { label: 'Necklaces', to: '/products/types/Necklaces' },
      { label: 'Pendants', to: '/products/types/Pendants' },
    ],
  },
  {
    title: 'Customer Care',
    links: [
      { label: 'Shipping & Delivery', to: '/shipping' },
      { label: 'Returns & Exchanges', to: '/returns' },
      { label: 'Ring Size Guide', to: '/help#ring-size-guide' },
      { label: 'Jewelry Care', to: '/help#jewelry-care' },
      { label: 'Contact support', to: '/support' },
    ],
  },
  {
    title: 'About',
    links: [
      { label: 'Our Story', to: '/about-us' },
      { label: 'Materials', to: '/about-us' },
      { label: 'Made to Order', to: '/about-us' },
      { label: 'Sustainability', to: '/about-us' },
      { label: 'Help Center', to: '/support' },
    ],
  },
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Footer = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedEmail = (user?.email || email).trim().toLowerCase();

    if (!emailRegex.test(normalizedEmail)) {
      message.error('Please enter a valid email address.');
      return;
    }

    try {
      setIsSubmitting(true);

      if (user?.id) {
        await dispatch(
          updateEmailPreference({
            userId: user.id,
            marketingEmailsOptIn: true,
          })
        ).unwrap();
      } else {
        await dispatch(subscribeMarketingEmail({ email: normalizedEmail })).unwrap();
      }

      setEmail('');
      message.success('You are subscribed to new product updates.');
    } catch (error) {
      message.error(typeof error === 'string' ? error : 'Failed to subscribe this email.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AntdFooter className="custom-footer">
      <div className="footer-container">
        <div className="footer-site-title">TrueKarat</div>

        <div className="footer-main">
          <div className="footer-brand">
            <h2>Fine jewelry catalog</h2>
            <p>
              Material-aware jewelry shopping with clear variant pricing, stock,
              care information, and checkout built for the US market.
            </p>
            <div className="footer-contact">
              <a href="mailto:support@truekarat.com">support@truekarat.com</a>
              <a href="tel:+18006910952">1.800.691.0952</a>
            </div>
          </div>

          <nav className="footer-links" aria-label="Footer navigation">
            {footerColumns.map((column) => (
              <div className="footer-column" key={column.title}>
                <h3>{column.title}</h3>
                <ul>
                  {column.links.map((link) => (
                    <li key={`${column.title}-${link.label}`}>
                      <Link to={link.to}>{link.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="footer-newsletter">
            <h3>Stay in the loop</h3>
            <p>Get new drops, care notes, and material updates.</p>
            <form
              className="footer-newsletter__form"
              onSubmit={handleNewsletterSubmit}
            >
              <input
                type="email"
                placeholder={user?.email || 'Email address'}
                aria-label="Email address"
                value={user?.id ? user.email || '' : email}
                disabled={Boolean(user?.id) || isSubmitting}
                onChange={(event) => setEmail(event.target.value)}
              />
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Joining...' : user?.marketingEmailsOptIn ? 'Joined' : 'Join'}
              </button>
            </form>
          </div>
        </div>

        <div className="footer-bottom">
          <span>© {new Date().getFullYear()} TrueKarat. All rights reserved.</span>
          <div className="footer-legal">
            <Link to="/privacy">Privacy policy</Link>
            <Link to="/terms">Terms of service</Link>
            <Link to="/returns">Returns and refunds</Link>
            <Link to="/shipping">Shipping and delivery</Link>
            <Link to="/cookies">Cookie preferences</Link>
            <Link to="/accessibility">Accessibility support</Link>
          </div>
        </div>
      </div>
    </AntdFooter>
  );
};

export default Footer;
